import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faBoxes, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { ErrorService } from '../../../../../../@core/utils/error.service';
import { InventoryService } from '../../../../pick-plan/services/inventory.service';
import { InventoryLotDTO } from '../../../models/inventory-lot';
import { InventoryItemDTO } from '../../../models/item';

export interface LotEditProductData {
  lot: InventoryLotDTO;
}

@Component({
  selector: 'lot-edit-product-popup',
  templateUrl: './lot-edit-product-popup.component.html',
  styleUrls: ['./lot-edit-product-popup.component.scss']
})
export class LotEditProductPopupComponent implements OnInit {

  /** Closes with `true` once the lot's product code is saved. */
  static open(dialog: MatDialog, data: LotEditProductData): MatDialogRef<LotEditProductPopupComponent, boolean> {
    return dialog.open(LotEditProductPopupComponent, { data, width: '560px', maxWidth: '95vw' });
  }

  public faBoxes = faBoxes;
  public faCheck = faCheck;
  public faXmark = faXmark;

  public lot: InventoryLotDTO;
  public inventoryItems: InventoryItemDTO[] = [];
  public filteredInventoryItems: InventoryItemDTO[] = [];
  public selectedProductId: number;
  public productControl = new FormControl<InventoryItemDTO | string>('');
  public isLoading: boolean = false;
  public originalProductName: string;

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LotEditProductData,
    private dialogRef: MatDialogRef<LotEditProductPopupComponent, boolean>,
    private inventoryService: InventoryService,
    private errorService: ErrorService
  ) {
    this.lot = data?.lot ?? {};
    this.selectedProductId = this.lot.defaultItemId;
    this.originalProductName = this.getProductCodesFromLot(this.lot);
  }

  ngOnInit() {
    this.isLoading = true;

    this.inventoryService.getInventoryItems().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: items => {
        this.inventoryItems = items ?? [];
        this.filteredInventoryItems = this.inventoryItems;

        const currentItem = this.inventoryItems.find(item => item.id === this.lot.defaultItemId);
        if (currentItem) {
          this.originalProductName = currentItem.name;
          this.productControl.setValue(currentItem);
        }

        this.isLoading = false;
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });

    this.productControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.filterProducts(value);
    });
  }

  displayProduct(item: InventoryItemDTO): string {
    return item?.name ?? '';
  }

  onProductSelected(event: MatAutocompleteSelectedEvent) {
    const selectedItem: InventoryItemDTO = event.option.value;
    this.selectedProductId = selectedItem?.id;
  }

  trackByItem(_: number, item: InventoryItemDTO): number | undefined {
    return item.id;
  }

  save() {
    if (!this.selectedProductId || !this.lot.id)
      return;

    this.isLoading = true;
    this.inventoryService.bulkUpdateProductCodeByLot(this.lot.id, this.selectedProductId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isLoading = false;
        this.dialogRef.close(true);
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }

  cancel() {
    this.dialogRef.close(false);
  }

  private filterProducts(value: InventoryItemDTO | string | null) {
    if (typeof value !== 'string' || !value) {
      this.filteredInventoryItems = this.inventoryItems;
      return;
    }
    const filterValue = value.toLowerCase();
    this.filteredInventoryItems = this.inventoryItems.filter(item => item.name?.toLowerCase().includes(filterValue));
  }

  private getProductCodesFromLot(lot: InventoryLotDTO): string {
    const productCodes = (lot.summaryHums ?? [])
      .flatMap(hum => hum.summaryHuds ?? [])
      .map(hud => hud.itemName)
      .filter(name => !!name);

    return productCodes.length
      ? [...new Set(productCodes)].join(', ')
      : lot.defaultItemName || lot.inventoryItem || '';
  }
}
